/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState, useRef } from "react";
import { api } from "../../services/api";
import logoImg from "../../assets/logo-red.png";
import nightVisionSound from "../../assets/splinter-cell-night-vision-goggle-sound-effect.mp3";
import "./styles.css";

interface Costumer {
  id: string;
  name: string;
  address: string;
  number: string;
  district: string;
  city: string;
  state: string;
  latitude: number;
  longitude: number;
  zone?: {
    id: string;
    name: string;
    color?: string;
  };
}

interface Zone {
  id: string;
  name: string;
  status: string;
  color?: string;
}

interface Report {
  id: string;
  code: string;
  begin: string;
  status: string;
  sent: boolean;
  costumer: {
    name: string;
  };
}

// Componente do Mapa
function SecretMapComponent({
  costumers,
  selectedZone,
  onSelectCostumer,
}: {
  costumers: Costumer[];
  selectedZone: string;
  onSelectCostumer: (costumer: Costumer | null) => void;
}) {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<any>(null);
  const markersRef = useRef<any[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const initialBoundsSetRef = useRef(false);

  const isValidCoordinate = (lat: number, lng: number): boolean => {
    return (
      typeof lat === "number" &&
      typeof lng === "number" &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      lat !== 0 &&
      lng !== 0 &&
      lat >= -90 &&
      lat <= 90 &&
      lng >= -180 &&
      lng <= 180
    );
  };

  const filteredCostumers = costumers
    .filter(costumer =>
      isValidCoordinate(costumer.latitude, costumer.longitude),
    )
    .filter(costumer => !selectedZone || costumer.zone?.id === selectedZone);

  // Carrega o Leaflet
  useEffect(() => {
    if ((window as any).L) {
      setIsLoaded(true);
      return;
    }

    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
    document.head.appendChild(link);

    const script = document.createElement("script");
    script.src = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";
    script.onload = () => setIsLoaded(true);
    document.head.appendChild(script);
  }, []);

  // Inicializa o mapa com estilo escuro
  useEffect(() => {
    if (
      isLoaded &&
      mapRef.current &&
      !mapInstanceRef.current &&
      (window as any).L
    ) {
      const defaultCenter: [number, number] = [-23.5505, -46.6333];

      mapInstanceRef.current = (window as any).L.map(mapRef.current, {
        zoomControl: false,
      }).setView(defaultCenter, 10);

      // Tile layer escuro (CartoDB Dark Matter)
      (window as any).L.tileLayer(
        "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png",
        {
          attribution:
            '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: "abcd",
          maxZoom: 20,
        },
      ).addTo(mapInstanceRef.current);

      // Adiciona controle de zoom customizado
      (window as any).L.control
        .zoom({
          position: "bottomright",
        })
        .addTo(mapInstanceRef.current);

      // Marcador da sede Luvilimp - Prédio comercial
      // Coordenadas do CEP 07195-270 (Jardim Monte Carmelo, Guarulhos)
      const sedeCoords: [number, number] = [
        -23.444481143620596, -46.50891456126984,
      ];
      const sedeIcon = (window as any).L.divIcon({
        className: "sede-marker",
        html: `
          <div style="
            display: flex;
            flex-direction: column;
            align-items: center;
            cursor: pointer;
          ">
            <svg width="20" height="26" viewBox="0 0 32 40" style="filter: drop-shadow(0 0 6px rgba(198, 40, 40, 0.9)) drop-shadow(0 0 10px rgba(198, 40, 40, 0.6));">
              <!-- Prédio principal -->
              <rect x="4" y="8" width="24" height="32" fill="#C62828" rx="1"/>
              <!-- Topo do prédio -->
              <rect x="6" y="4" width="20" height="6" fill="#C62828" rx="1"/>
              <!-- Antena -->
              <rect x="15" y="0" width="2" height="5" fill="#C62828"/>
              <!-- Janelas - Linha 1 -->
              <rect x="7" y="12" width="4" height="3" fill="#0A0A0A"/>
              <rect x="14" y="12" width="4" height="3" fill="#0A0A0A"/>
              <rect x="21" y="12" width="4" height="3" fill="#0A0A0A"/>
              <!-- Janelas - Linha 2 -->
              <rect x="7" y="18" width="4" height="3" fill="#0A0A0A"/>
              <rect x="14" y="18" width="4" height="3" fill="#0A0A0A"/>
              <rect x="21" y="18" width="4" height="3" fill="#0A0A0A"/>
              <!-- Janelas - Linha 3 -->
              <rect x="7" y="24" width="4" height="3" fill="#0A0A0A"/>
              <rect x="14" y="24" width="4" height="3" fill="#0A0A0A"/>
              <rect x="21" y="24" width="4" height="3" fill="#0A0A0A"/>
              <!-- Porta -->
              <rect x="12" y="32" width="8" height="8" fill="#0A0A0A" rx="1"/>
            </svg>
            <div style="
              background: #C62828;
              color: white;
              font-size: 7px;
              padding: 1px 4px;
              font-family: 'Orbitron', sans-serif;
              letter-spacing: 1px;
              margin-top: 1px;
              white-space: nowrap;
              box-shadow: 0 0 8px rgba(198, 40, 40, 0.8);
            ">LUVILIMP HQ</div>
          </div>
        `,
        iconSize: [50, 38],
        iconAnchor: [25, 38],
      });

      (window as any).L.marker(sedeCoords, { icon: sedeIcon })
        .addTo(mapInstanceRef.current)
        .bindPopup(
          `
          <div style="
            background: #0D0D0D;
            color: #FFF;
            padding: 12px;
            font-family: 'Rajdhani', sans-serif;
            min-width: 200px;
          ">
            <div style="
              font-family: 'Orbitron', sans-serif;
              font-size: 12px;
              color: #C62828;
              margin-bottom: 8px;
              letter-spacing: 2px;
            ">// HEADQUARTERS</div>
            <div style="font-weight: 600; font-size: 14px; margin-bottom: 4px;">LUVILIMP</div>
            <div style="font-size: 11px; color: #888;">R. Luís Moreira, 121</div>
            <div style="font-size: 11px; color: #888;">Jardim Monte Carmelo</div>
            <div style="font-size: 11px; color: #888;">Guarulhos - SP, 07195-270</div>
          </div>
        `,
          {
            className: "sede-popup",
          },
        );
    }
  }, [isLoaded]);

  // Atualiza marcadores
  useEffect(() => {
    if (!isLoaded || !mapInstanceRef.current) return;

    // Remove marcadores antigos
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];

    if (filteredCostumers.length === 0) return;

    const { L } = window as any;

    // Função para converter cor hex para rgba
    const hexToRgba = (hex: string, alpha: number) => {
      const r = parseInt(hex.slice(1, 3), 16);
      const g = parseInt(hex.slice(3, 5), 16);
      const b = parseInt(hex.slice(5, 7), 16);
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    };

    // Ícone triangular com cor da zona e efeito neon
    const createTriangleIcon = (isSelected = false, zoneColor = "#C62828") => {
      const size = isSelected ? 24 : 16;
      const glowSize = isSelected ? "10px" : "6px";
      const glowColor = hexToRgba(zoneColor, 0.9);
      return L.divIcon({
        className: "securoserv-marker",
        html: `
          <div class="triangle-marker ${isSelected ? "selected" : ""}" style="
            width: 0;
            height: 0;
            border-left: ${size / 2}px solid transparent;
            border-right: ${size / 2}px solid transparent;
            border-bottom: ${size}px solid ${zoneColor};
            filter: drop-shadow(0 0 ${glowSize} ${glowColor}) drop-shadow(0 0 ${
          isSelected ? "15px" : "3px"
        } ${glowColor});
            cursor: pointer;
            transition: all 0.3s ease;
          "></div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size],
      });
    };

    // Armazena a cor de cada marcador para uso no click
    const markerColorMap = new Map<any, string>();

    filteredCostumers.forEach(costumer => {
      const color = costumer.zone?.color || "#C62828";
      const marker = L.marker([costumer.latitude, costumer.longitude], {
        icon: createTriangleIcon(false, color),
      }).addTo(mapInstanceRef.current);

      markerColorMap.set(marker, color);

      marker.on("click", () => {
        onSelectCostumer(costumer);
        // Atualiza todos os marcadores para não selecionados
        markersRef.current.forEach(m => {
          const markerColor = markerColorMap.get(m) || "#C62828";
          m.setIcon(createTriangleIcon(false, markerColor));
        });
        // Marca o atual como selecionado
        marker.setIcon(createTriangleIcon(true, color));
      });

      markersRef.current.push(marker);
    });

    // Ajusta bounds apenas na primeira vez
    if (filteredCostumers.length > 0 && !initialBoundsSetRef.current) {
      const bounds = L.latLngBounds(
        filteredCostumers.map(c => [c.latitude, c.longitude]),
      );
      mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      initialBoundsSetRef.current = true;
    }
  }, [isLoaded, filteredCostumers, onSelectCostumer]);

  if (!isLoaded) {
    return (
      <div className="secret-map-loading">
        <div className="loading-scanner" />
        <span>INITIALIZING SATELLITE FEED...</span>
      </div>
    );
  }

  return (
    <div className="secret-map-container">
      <div ref={mapRef} className="secret-map" />
      <div className="map-grid-overlay" />
      <div className="map-scanline" />
    </div>
  );
}

// Componente do Painel de Informações
function InfoPanel({
  costumer,
  onClose,
  onViewReports,
  showReports,
}: {
  costumer: Costumer | null;
  onClose: () => void;
  onViewReports: () => void;
  showReports: boolean;
}) {
  if (!costumer) {
    return (
      <div className="info-panel">
        <div className="panel-header">
          <span>&#47;&#47; LOCATION DATA</span>
        </div>
        <div className="panel-content empty">
          <div className="empty-icon">◢</div>
          <p>SELECT A LOCATION ON THE MAP</p>
          <p className="subtext">Click on any marker to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div className="info-panel active">
      <div className="panel-header">
        <span>&#47;&#47; LOCATION DATA</span>
        <button type="button" className="close-btn" onClick={onClose}>
          ×
        </button>
      </div>
      <div className="panel-content">
        <div className="location-image">
          <div className="image-placeholder">
            <span>◢</span>
          </div>
          <div className="image-label">SATELLITE VIEW</div>
        </div>

        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">DESIGNATION</span>
            <span>{costumer.name.toUpperCase()}</span>
          </div>
          <div className="info-item">
            <span className="info-label">SECTOR</span>
            <span>{costumer.zone?.name?.toUpperCase() || "UNASSIGNED"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">ADDRESS</span>
            <span>
              {costumer.address}, {costumer.number}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">DISTRICT</span>
            <span>{costumer.district?.toUpperCase() || "N/A"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">CITY</span>
            <span>{costumer.city?.toUpperCase() || "N/A"}</span>
          </div>
          <div className="info-item">
            <span className="info-label">STATE</span>
            <span>{costumer.state?.toUpperCase() || "N/A"}</span>
          </div>
          <div className="info-item full">
            <span className="info-label">COORDINATES</span>
            <span className="coords">
              {costumer.latitude.toFixed(6)}, {costumer.longitude.toFixed(6)}
            </span>
          </div>
        </div>

        <div className="panel-actions">
          <button type="button" className="action-btn primary">
            <span>◢</span> ACCESS RECORDS
          </button>
          <button
            type="button"
            className={`action-btn ${showReports ? "active" : "secondary"}`}
            onClick={onViewReports}
          >
            VIEW REPORTS
          </button>
        </div>
      </div>
    </div>
  );
}

// Componente do Painel de Relatórios
function ReportsPanel({
  reports,
  loadingReports,
  onClose,
}: {
  reports: Report[];
  loadingReports: boolean;
  onClose: () => void;
}) {
  const formatDate = (dateStr: string) => {
    if (!dateStr) return "N/A";
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("pt-BR");
  };

  const getStatusLabel = (status: string) => {
    const statusMap: Record<string, string> = {
      P: "PENDING",
      A: "APPROVED",
      R: "REJECTED",
      C: "CANCELLED",
    };
    return statusMap[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colorMap: Record<string, string> = {
      P: "#FFA726",
      A: "#4CAF50",
      R: "#C62828",
      C: "#666",
    };
    return colorMap[status] || "#888";
  };

  return (
    <div className="reports-panel side">
      <div className="panel-header">
        <span>&#47;&#47; OPERATION REPORTS</span>
        <div className="panel-header-right">
          <span className="report-count">{reports.length}</span>
          <button type="button" className="close-btn" onClick={onClose}>
            ×
          </button>
        </div>
      </div>
      <div className="reports-list">
        {loadingReports && (
          <div className="reports-loading">
            <div className="loading-scanner" />
            <span>LOADING DATA...</span>
          </div>
        )}
        {!loadingReports && reports.length === 0 && (
          <div className="reports-empty">
            <span>NO REPORTS FOUND</span>
          </div>
        )}
        {!loadingReports &&
          reports.length > 0 &&
          reports.map(report => (
            <div key={report.id} className="report-item">
              <div className="report-header">
                <span className="report-code">#{report.code}</span>
                <span
                  className="report-status"
                  style={{ color: getStatusColor(report.status) }}
                >
                  {getStatusLabel(report.status)}
                </span>
              </div>
              <div className="report-date">
                <span className="date-label">DATE:</span>
                <span>{formatDate(report.begin)}</span>
              </div>
              <div className="report-sent">
                <span
                  className={`sent-indicator ${
                    report.sent ? "sent" : "not-sent"
                  }`}
                >
                  {report.sent ? "◉ SENT" : "○ NOT SENT"}
                </span>
              </div>
            </div>
          ))}
      </div>
    </div>
  );
}

// Componente de Loading com frases em sequência
const LOADING_MESSAGES = [
  "ESTABLISHING SECURE CONNECTION...",
  "ENCRYPTING DATA STREAM...",
  "LOADING SATELLITE IMAGERY...",
  "SYNCING LOCATION DATABASE...",
  "INITIALIZING TRACKING SYSTEM...",
  "CONNECTION ESTABLISHED",
];

// Áudio global pré-carregado
let nightVisionAudio: HTMLAudioElement | null = null;

function playNightVisionSound() {
  try {
    // Sempre cria uma nova instância para evitar problemas de cache
    nightVisionAudio = new Audio(nightVisionSound);
    nightVisionAudio.volume = 0.6;
    nightVisionAudio.currentTime = 0;

    const playPromise = nightVisionAudio.play();
    if (playPromise !== undefined) {
      playPromise.catch(() => {
        // Autoplay bloqueado pelo navegador
      });
    }
  } catch {
    // Erro ao criar/tocar áudio
  }
}

function LoadingScreen({ onComplete }: { onComplete: () => void }) {
  const [messageIndex, setMessageIndex] = useState(0);
  const [showFinalFlash, setShowFinalFlash] = useState(false);
  const onCompleteRef = useRef(onComplete);

  // Mantém referência atualizada sem causar re-render
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  useEffect(() => {
    const interval = setInterval(() => {
      setMessageIndex(prev => {
        if (prev < LOADING_MESSAGES.length - 1) {
          return prev + 1;
        }
        return prev;
      });
    }, 600);

    // Após 3.2s, toca o som e finaliza
    const finalTimeout = setTimeout(() => {
      setShowFinalFlash(true);

      // Toca o som
      playNightVisionSound();

      // Aguarda 600ms e mostra o mapa
      setTimeout(() => {
        onCompleteRef.current();
      }, 600);
    }, 3200);

    return () => {
      clearInterval(interval);
      clearTimeout(finalTimeout);
    };
  }, []);

  return (
    <div className="secret-page">
      <div className={`loading-screen ${showFinalFlash ? "flash" : ""}`}>
        <div className="loading-logo">
          <img src={logoImg} alt="Logo" />
        </div>
        <div className="loading-text">{LOADING_MESSAGES[messageIndex]}</div>
        <div className="loading-bar">
          <div className="loading-progress" />
        </div>
      </div>
    </div>
  );
}

// Componente Principal
export function SecretMap() {
  const [costumers, setCostumers] = useState<Costumer[]>([]);
  const [zones, setZones] = useState<Zone[]>([]);
  const [selectedZone, setSelectedZone] = useState<string>("");
  const [selectedCostumer, setSelectedCostumer] = useState<Costumer | null>(
    null,
  );
  const [showLoading, setShowLoading] = useState(true);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [showReports, setShowReports] = useState(false);
  const [reports, setReports] = useState<Report[]>([]);
  const [loadingReports, setLoadingReports] = useState(false);

  // Muda o favicon quando entra na página secreta
  useEffect(() => {
    const originalFavicon =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const originalHref = originalFavicon?.href;

    // Cria ou atualiza o favicon
    let faviconLink =
      document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (!faviconLink) {
      faviconLink = document.createElement("link");
      faviconLink.rel = "icon";
      document.head.appendChild(faviconLink);
    }
    faviconLink.href = logoImg;

    // Muda o título da página
    const originalTitle = document.title;
    document.title = "// SECURE MAP //";

    // Restaura ao sair
    return () => {
      if (faviconLink && originalHref) {
        faviconLink.href = originalHref;
      }
      document.title = originalTitle;
    };
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const loadData = async () => {
      try {
        const [costumersResponse, zonesResponse] = await Promise.all([
          api.get("/costumers/map"),
          api.get("/zones"),
        ]);
        setCostumers(costumersResponse.data);
        setZones(zonesResponse.data.filter((z: Zone) => z.status === "A"));
      } catch {
        // Error handling
      }
    };

    loadData();
  }, []);

  const stats = {
    total: costumers.length,
    active: costumers.filter(c => c.zone).length,
    unassigned: costumers.filter(c => !c.zone).length,
  };

  const handleSelectCostumer = (costumer: Costumer | null) => {
    setSelectedCostumer(costumer);
    setShowReports(false);
    setReports([]);
  };

  const handleViewReports = async () => {
    if (!selectedCostumer) return;

    if (showReports) {
      setShowReports(false);
      return;
    }

    setShowReports(true);
    setLoadingReports(true);

    try {
      const response = await api.get("/techVisitReport", {
        params: {
          costumer_id: selectedCostumer.id,
          limit: 50,
        },
      });
      setReports(response.data.data || []);
    } catch {
      setReports([]);
    } finally {
      setLoadingReports(false);
    }
  };

  if (showLoading) {
    return <LoadingScreen onComplete={() => setShowLoading(false)} />;
  }

  return (
    <div className="secret-page">
      {/* Top Bar */}
      <header className="secret-header">
        <div className="header-left">
          <div className="logo">
            <img src={logoImg} alt="Logo" className="logo-icon" />
            <span className="logo-text">
              LUVI<span className="accent">APP</span>
            </span>
          </div>
        </div>
        <nav className="header-nav">
          <button type="button" className="nav-item active">
            LOCATIONS
          </button>
          <button type="button" className="nav-item">
            OPERATIONS
          </button>
          <button type="button" className="nav-item">
            PERSONNEL
          </button>
          <button type="button" className="nav-item">
            REPORTS
          </button>
        </nav>
        <div className="header-right">
          <div className="system-time">
            <span className="time-label">SYS TIME</span>
            <span className="time-value">
              {currentTime.toLocaleTimeString("pt-BR", { hour12: false })}
            </span>
          </div>
          <div className="user-badge">
            <span className="badge-icon">◉</span>
            <span className="badge-text">ADMIN</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="secret-main">
        {/* Stats Bar */}
        <div className="stats-bar">
          <div className="stat-item">
            <span className="stat-label">TOTAL LOCATIONS</span>
            <span className="stat-value">{stats.total}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-label">ASSIGNED</span>
            <span className="stat-value green">{stats.active}</span>
          </div>
          <div className="stat-divider" />
          <div className="stat-item">
            <span className="stat-label">UNASSIGNED</span>
            <span className="stat-value red">{stats.unassigned}</span>
          </div>
          <div className="filter-section">
            <span className="filter-label">FILTER BY SECTOR:</span>
            <select
              value={selectedZone}
              onChange={e => setSelectedZone(e.target.value)}
              className="filter-select"
            >
              <option value="">ALL SECTORS</option>
              {zones.map(zone => (
                <option key={zone.id} value={zone.id}>
                  {zone.name.toUpperCase()}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Map Area */}
        <div className="content-area">
          <SecretMapComponent
            costumers={costumers}
            selectedZone={selectedZone}
            onSelectCostumer={handleSelectCostumer}
          />
          <div className="side-panels">
            <InfoPanel
              costumer={selectedCostumer}
              onClose={() => handleSelectCostumer(null)}
              onViewReports={handleViewReports}
              showReports={showReports}
            />
            {showReports && (
              <ReportsPanel
                reports={reports}
                loadingReports={loadingReports}
                onClose={() => setShowReports(false)}
              />
            )}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="secret-footer">
        <span>
          ◢ LUVIAPP NETWORK // ENCRYPTED CONNECTION //{" "}
          {currentTime.toLocaleDateString("pt-BR")}
        </span>
        <span className="footer-status">
          <span className="status-dot" />
          SYSTEM OPERATIONAL
        </span>
      </footer>
    </div>
  );
}
